import { Link } from 'react-router-dom'
import { Megaphone, Bot, Users, Receipt, ArrowRight, MessageSquareText, Send, ArrowLeftRight } from 'lucide-react'
import { KpiCard } from '@/components/ui/KpiCard'
import { Chip } from '@/components/ui/Chip'

interface AreaConfig {
  to: string
  accent: string
  icon: typeof Megaphone
  iconBg: string
  iconColor: string
  title: string
  description: string
  badge: { text: string; variant: 'emerald' | 'amber' | 'rose' | 'blue' }
  stats: { label: string; value: string; valueClass?: string }[]
}

const areas: AreaConfig[] = [
  {
    to: '/whatsapp/campanas',
    accent: '#3b82f6',
    icon: Megaphone,
    iconBg: 'from-blue-50 to-blue-100',
    iconColor: 'text-blue-600',
    title: 'Campañas',
    description: 'Outbound masivo con templates Meta. Preventiva + mora. Programadas automáticamente.',
    badge: { text: '4 activas', variant: 'blue' },
    stats: [
      { label: 'Enviadas hoy', value: '1,247' },
      { label: 'Respondidas', value: '28%', valueClass: 'text-amber-500' },
      { label: 'Recupero', value: '$2.1k', valueClass: 'text-emerald-600' },
    ],
  },
  {
    to: '/whatsapp/autoservicio',
    accent: '#10b981',
    icon: Bot,
    iconBg: 'from-emerald-50 to-emerald-100',
    iconColor: 'text-emerald-600',
    title: 'Autoservicio',
    description: 'Bandeja de chats en vivo con copilot IA. Chatwoot integrado + supervisión aprendizaje.',
    badge: { text: '42 chats · 8 drafts', variant: 'emerald' },
    stats: [
      { label: 'Chats activos', value: '42' },
      { label: 'Drafts pendientes', value: '8', valueClass: 'text-amber-500' },
      { label: 'FAQs auto', value: '127', valueClass: 'text-emerald-600' },
    ],
  },
  {
    to: '/whatsapp/humana',
    accent: '#d97a1c',
    icon: Users,
    iconBg: 'from-amber-50 to-amber-100',
    iconColor: 'text-amber-600',
    title: 'Gestión humana',
    description: 'Chats escalados — pidió asesor, caso complejo, o IA no pudo resolver.',
    badge: { text: '3 urgentes', variant: 'rose' },
    stats: [
      { label: 'En cola', value: '14' },
      { label: 'Espera prom.', value: '6min', valueClass: 'text-amber-500' },
      { label: 'Resueltos', value: '89%', valueClass: 'text-emerald-600' },
    ],
  },
  {
    to: '/whatsapp/revision',
    accent: '#e11d48',
    icon: Receipt,
    iconBg: 'from-rose-50 to-rose-100',
    iconColor: 'text-rose-600',
    title: 'Revisión',
    description: 'Comprobantes que la IA no validó al 100%. Editás campos problemáticos y aprobás.',
    badge: { text: '3 pendientes', variant: 'amber' },
    stats: [
      { label: 'Pendientes', value: '3' },
      { label: 'Auto-valid.', value: '82%', valueClass: 'text-emerald-600' },
      { label: 'Hoy', value: '$8.9k', valueClass: 'text-amber-500' },
    ],
  },
]

export function WhatsappOverview() {
  return (
    <div className="view-transition max-w-7xl mx-auto px-10 pt-8 pb-16">
      {/* Hero */}
      <div className="relative rounded-2xl p-8 mb-10 overflow-hidden grain-overlay"
        style={{
          background:
            'radial-gradient(ellipse at top left, rgba(244,163,64,0.08), transparent 60%), radial-gradient(ellipse at bottom right, rgba(28,60,107,0.05), transparent 60%)',
        }}
      >
        <div className="relative z-10 flex items-end justify-between gap-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="pulse-live" />
              <span className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">
                Sistema activo
              </span>
            </div>
            <h1 className="font-display text-6xl font-semibold text-navy-700 leading-none">WhatsApp</h1>
            <p className="text-slate-600 mt-3 text-base max-w-xl">
              La operación conversacional en vivo. 4 áreas, un solo dashboard.
            </p>
          </div>
          <div className="hidden lg:flex items-center gap-6 pr-4">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                Cobrado hoy
              </div>
              <div className="font-display text-4xl font-semibold text-navy-700">$8,921</div>
              <div className="text-xs text-emerald-600 font-medium mt-0.5">↑ 18% vs ayer</div>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs globales del día */}
      <div className="grid grid-cols-4 gap-4 mb-10">
        <KpiCard
          label="Chats del día"
          value="847"
          sub="personas únicas"
          icon={<MessageSquareText className="w-3 h-3" />}
          delta={{ value: '+12%', up: true }}
        />
        <KpiCard
          label="Mensajes enviados"
          value="1,772"
          sub="los que mandamos"
          icon={<Send className="w-3 h-3" />}
        />
        <KpiCard
          label="Nos escribieron"
          value="523"
          sub="iniciaron el chat · 62%"
          color="emerald"
          icon={<ArrowLeftRight className="w-3 h-3" />}
        />
        <KpiCard
          label="Nosotros iniciamos"
          value="324"
          sub="campañas outbound · 38%"
          color="amber"
          icon={<Send className="w-3 h-3" />}
        />
      </div>

      {/* 4 áreas */}
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <h2 className="font-display text-3xl font-semibold text-navy-700">Las 4 áreas</h2>
          <p className="text-sm text-slate-500 mt-1">Click en cualquiera para entrar al detalle</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5">
        {areas.map(area => {
          const Icon = area.icon
          return (
            <Link
              key={area.to}
              to={area.to}
              className="area-card group"
              style={{ ['--accent' as string]: area.accent }}
            >
              <div className="flex items-start justify-between mb-5">
                <div
                  className={`w-14 h-14 bg-gradient-to-br ${area.iconBg} rounded-xl flex items-center justify-center shadow-inner`}
                >
                  <Icon className={`w-7 h-7 ${area.iconColor}`} />
                </div>
                <Chip variant={area.badge.variant}>{area.badge.text}</Chip>
              </div>
              <h3 className="font-display text-2xl font-semibold text-navy-700">{area.title}</h3>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">{area.description}</p>
              <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-3 gap-4">
                {area.stats.map(s => (
                  <div key={s.label}>
                    <div
                      className={`number-display text-2xl ${s.valueClass ?? 'text-navy-700'}`}
                    >
                      {s.value}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition" style={{ color: area.accent }}>
                Explorar <ArrowRight className="w-3 h-3" />
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}
